import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fuelAPI, vehiclesAPI, driversAPI } from '../services/api';
import { toast } from 'sonner';
import { 
  Fuel, 
  Plus, 
  Loader2, 
  Calendar,
  TrendingUp,
  Gauge,
  Download,
  Edit,
  Trash2,
  Eye,
  Upload,
  File,
  Truck,
  User,
  FileSpreadsheet,
  BarChart3,
  Droplets,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STANDARD_CONSUMPTION = 2.2;

// Fuel Record Detail Dialog
const FuelDetailDialog = ({ open, onClose, record, onEdit, onDelete }) => {
  if (!record) return null;

  const isPending = !record.consumption_calculated;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Fuel className="w-5 h-5 text-amber-500" />
            Fuel Record Details
            {isPending && (
              <Badge variant="outline" className="ml-2 text-amber-600 border-amber-400">
                <Clock className="w-3 h-3 mr-1" />
                Pending Calculation
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="text-xs text-slate-500">Fill Date</Label>
              <p className="font-semibold">{record.fill_date?.slice(0, 10)}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Registration No</Label>
              <p className="font-mono font-bold">{record.registration_number}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Driver</Label>
              <p>{record.driver_name || 'N/A'}</p>
            </div>
          </div>

          {/* Odometer Info */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div>
              <Label className="text-xs text-slate-500">Current Odometer (at fill)</Label>
              <p className="text-lg font-semibold">{record.current_odometer?.toLocaleString()} km</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Closing Odometer (next fill)</Label>
              <p className="text-lg font-semibold">
                {record.closing_odometer ? `${record.closing_odometer.toLocaleString()} km` : 
                  <span className="text-amber-600">Pending</span>}
              </p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Travelled KM</Label>
              <p className="text-lg font-bold text-blue-600">
                {record.travelled_km ? `${record.travelled_km.toLocaleString()} km` : 
                  <span className="text-amber-600">Pending</span>}
              </p>
            </div>
          </div>

          {/* Fuel & Consumption */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div>
              <Label className="text-xs text-slate-500">Litres Refuelled</Label>
              <p className="text-xl font-bold text-amber-600">{record.litres_refuelled} L</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Actual Consumption</Label>
              {record.actual_consumption ? (
                <p className={`text-xl font-bold ${record.actual_consumption >= STANDARD_CONSUMPTION ? 'text-emerald-600' : 'text-red-600'}`}>
                  {record.actual_consumption} KM/L
                </p>
              ) : (
                <p className="text-xl font-bold text-amber-600">Pending</p>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500">Standard Consumption</Label>
              <p className="text-xl font-semibold text-slate-600">{STANDARD_CONSUMPTION} KM/L</p>
            </div>
          </div>

          {/* Consumption comparison */}
          {record.actual_consumption && (
            <div className={`p-3 rounded-lg ${record.actual_consumption >= STANDARD_CONSUMPTION ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
              <p className={`text-sm font-medium ${record.actual_consumption >= STANDARD_CONSUMPTION ? 'text-emerald-700' : 'text-red-700'}`}>
                {record.actual_consumption >= STANDARD_CONSUMPTION 
                  ? `✓ Consumption is ${((record.actual_consumption / STANDARD_CONSUMPTION - 1) * 100).toFixed(1)}% above standard`
                  : `⚠ Consumption is ${((1 - record.actual_consumption / STANDARD_CONSUMPTION) * 100).toFixed(1)}% below standard - Corrective action may be required`
                }
              </p>
            </div>
          )}

          {isPending && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-700 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Consumption will be calculated when the next fuel fill-up is recorded for this vehicle.
              </p>
            </div>
          )}

          {/* Comments */}
          {record.comments && (
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Comments</Label>
              <p className="text-sm bg-slate-50 p-3 rounded">{record.comments}</p>
            </div>
          )}

          {/* Fuel Slip */}
          {record.fuel_slip_filename && (
            <div className="flex items-center gap-3 p-3 border rounded-lg">
              <File className="w-5 h-5 text-blue-500" />
              <span className="flex-1 text-sm font-medium">Fuel Slip</span>
              <a 
                href={`${API_URL}/api/uploads/fuel/${record.fuel_slip_filename}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline flex items-center gap-1"
              >
                <Download className="w-4 h-4" />
                Download
              </a>
            </div>
          )}

          {/* Meta info */}
          <div className="grid grid-cols-2 gap-4 p-3 bg-slate-100 rounded text-sm">
            <div>
              <span className="text-slate-500">Created by:</span>
              <span className="ml-2">{record.created_by_name || 'Unknown'}</span>
            </div>
            <div>
              <span className="text-slate-500">Created at:</span>
              <span className="ml-2">{record.created_at?.slice(0, 10)}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button variant="outline" onClick={() => { onEdit(record); onClose(); }}>
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </Button>
          <Button 
            variant="destructive" 
            onClick={() => {
              if (window.confirm('Delete this record? This may affect consumption calculations for adjacent records.')) {
                onDelete(record);
                onClose();
              }
            }}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Fuel Form Dialog - Only captures Current Odometer (not opening/closing)
const FuelFormDialog = ({ open, onClose, record, onSave, vehicles, drivers }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingSlip, setUploadingSlip] = useState(false);
  const [savedRecordId, setSavedRecordId] = useState(null);
  const [lastOdometerInfo, setLastOdometerInfo] = useState(null);
  
  const getInitialFormData = (rec) => {
    if (rec) {
      return {
        fill_date: rec.fill_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
        vehicle_id: rec.vehicle_id || '',
        driver_id: rec.driver_id || '',
        driver_name: rec.driver_name || '',
        current_odometer: rec.current_odometer || 0,
        litres_refuelled: rec.litres_refuelled || 0,
        comments: rec.comments || '',
      };
    }
    return {
      fill_date: new Date().toISOString().slice(0, 10),
      vehicle_id: '',
      driver_id: '',
      driver_name: '',
      current_odometer: 0,
      litres_refuelled: 0,
      comments: '',
    };
  };

  const [formData, setFormData] = useState(() => getInitialFormData(record));

  useEffect(() => {
    if (open) {
      setSavedRecordId(record?.id || null);
      setFormData(getInitialFormData(record));
      setLastOdometerInfo(null);
    }
  }, [open, record]);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Fetch last odometer when vehicle is selected (for reference)
  const handleVehicleChange = async (vehicleId) => {
    updateField('vehicle_id', vehicleId);
    
    if (vehicleId && !record) {
      try {
        const response = await fuelAPI.getLastOdometer(vehicleId);
        setLastOdometerInfo(response.data);
      } catch (error) {
        console.error('Error fetching last odometer:', error);
        setLastOdometerInfo(null);
      }
    }
  };

  // Auto-populate driver name when driver is selected
  const handleDriverChange = (driverId) => {
    updateField('driver_id', driverId);
    const driver = drivers.find(d => d.id === driverId);
    if (driver) {
      updateField('driver_name', `${driver.first_name || driver.user?.first_name || ''} ${driver.last_name || driver.user?.last_name || ''}`.trim() || driver.employee_number);
    }
  };

  const handleSave = async () => {
    if (!formData.vehicle_id) {
      toast.error('Please select a vehicle');
      return;
    }
    if (formData.current_odometer <= 0) {
      toast.error('Please enter the current odometer reading');
      return;
    }
    if (formData.litres_refuelled <= 0) {
      toast.error('Please enter litres refuelled');
      return;
    }
    
    // Validate odometer is higher than last fill
    if (lastOdometerInfo && formData.current_odometer <= lastOdometerInfo.last_odometer) {
      toast.error(`Current odometer must be higher than the last reading (${lastOdometerInfo.last_odometer} km)`);
      return;
    }

    setLoading(true);
    try {
      const result = await onSave(formData);
      if (result?.id) {
        setSavedRecordId(result.id);
      }
      onClose();
    } catch (error) {
      console.error('Error saving fuel record:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSlip = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!savedRecordId) {
      toast.error('Please save the record first before uploading fuel slip');
      return;
    }

    setUploadingSlip(true);
    try {
      await fuelAPI.uploadSlip(savedRecordId, file);
      toast.success('Fuel slip uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload fuel slip');
    } finally {
      setUploadingSlip(false);
    }
  };

  const trucks = vehicles.filter(v => 
    v.vehicle_type?.toLowerCase() === 'truck' || 
    v.vehicle_type?.toLowerCase() === 'horse' ||
    !v.vehicle_type?.toLowerCase().includes('trailer')
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Fuel className="w-5 h-5 text-amber-500" />
            {record ? 'Edit Fuel Record' : 'New Fuel Fill-up'}
          </DialogTitle>
          <DialogDescription>
            Record the fuel fill-up. Consumption will be calculated automatically when the next fill-up is recorded.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Fill Date *</Label>
              <Input
                type="date"
                value={formData.fill_date}
                onChange={(e) => updateField('fill_date', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Vehicle *</Label>
              <Select value={formData.vehicle_id || 'none'} onValueChange={(v) => handleVehicleChange(v === 'none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select vehicle</SelectItem>
                  {trucks.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.registration_number} - {v.make} {v.model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Driver</Label>
              <Select value={formData.driver_id || 'none'} onValueChange={(v) => handleDriverChange(v === 'none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select driver" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select driver</SelectItem>
                  {drivers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.first_name || d.user?.first_name || ''} {d.last_name || d.user?.last_name || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Odometer Section */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2 flex items-center gap-2">
              <Gauge className="w-4 h-4" />
              Odometer Reading
            </h3>
            
            {lastOdometerInfo && lastOdometerInfo.last_odometer > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <p className="text-blue-700">
                  <strong>Last recorded odometer:</strong> {lastOdometerInfo.last_odometer.toLocaleString()} km 
                  {lastOdometerInfo.last_fill_date && ` (on ${lastOdometerInfo.last_fill_date})`}
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Current Odometer (km) *</Label>
                <Input
                  type="number"
                  value={formData.current_odometer}
                  onChange={(e) => updateField('current_odometer', parseFloat(e.target.value) || 0)}
                  placeholder="Odometer at time of fill"
                />
                <p className="text-xs text-slate-500">Enter the odometer reading when filling</p>
              </div>
              <div className="space-y-2">
                <Label>Litres Refuelled *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.litres_refuelled}
                  onChange={(e) => updateField('litres_refuelled', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <p className="text-amber-700 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <strong>Note:</strong> Travelled KM and consumption will be calculated automatically when the next fill-up is recorded for this vehicle.
              </p>
            </div>
          </div>

          {/* Comments */}
          <div className="space-y-2">
            <Label>Comments</Label>
            <Textarea
              value={formData.comments}
              onChange={(e) => updateField('comments', e.target.value)}
              placeholder="Any additional notes..."
              rows={2}
            />
          </div>

          {/* Fuel Slip Upload */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 border-b pb-2 flex items-center gap-2">
              <File className="w-4 h-4" />
              Fuel Slip
            </h3>
            {!savedRecordId ? (
              <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                Save the record first to enable fuel slip upload.
              </p>
            ) : (
              <div className="flex items-center gap-3">
                <label>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleUploadSlip}
                    disabled={uploadingSlip}
                  />
                  <Button variant="outline" disabled={uploadingSlip} asChild>
                    <span>
                      {uploadingSlip ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                      Upload Fuel Slip
                    </span>
                  </Button>
                </label>
                {record?.fuel_slip_filename && (
                  <a 
                    href={`${API_URL}/api/uploads/fuel/${record.fuel_slip_filename}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <Download className="w-4 h-4" />
                    View Existing Slip
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {record ? 'Update' : 'Save'} Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Page Component
export const FuelSchedulePage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  const [records, setRecords] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const fetchData = async (month = '') => {
    try {
      setLoading(true);
      const params = month ? { month } : {};
      
      const [recordsRes, statsRes, vehiclesRes, driversRes, monthsRes] = await Promise.all([
        fuelAPI.list(params),
        fuelAPI.getStatistics(month),
        vehiclesAPI.list(),
        driversAPI.list(),
        fuelAPI.getAvailableMonths(),
      ]);
      
      setRecords(recordsRes.data || []);
      setStatistics(statsRes.data || {});
      setVehicles(vehiclesRes.data || []);
      setDrivers(driversRes.data || []);
      setAvailableMonths(monthsRes.data?.months || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load fuel data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedMonth);
  }, [selectedMonth]);

  const handleCreateRecord = async (data) => {
    try {
      let result;
      if (selectedRecord) {
        result = await fuelAPI.update(selectedRecord.id, data);
        toast.success('Fuel record updated successfully');
      } else {
        result = await fuelAPI.create(data, user.id, `${user.first_name} ${user.last_name}`);
        toast.success('Fuel record created successfully');
        
        // Show toast if previous fill was updated
        if (result.data?.previous_fill_updated) {
          if (result.data?.corrective_action_created) {
            toast.warning(`Previous fill consumption calculated: ${result.data.previous_fill_consumption} KM/L (below standard). Corrective action created.`, {
              duration: 6000,
            });
          } else {
            toast.info(`Previous fill consumption calculated: ${result.data.previous_fill_consumption} KM/L`, {
              duration: 4000,
            });
          }
        }
      }
      setSelectedRecord(null);
      fetchData(selectedMonth);
      return result.data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Failed to save fuel record';
      toast.error(errorMsg);
      throw error;
    }
  };

  const handleDelete = async (record) => {
    try {
      await fuelAPI.delete(record.id);
      toast.success('Fuel record deleted');
      fetchData(selectedMonth);
    } catch (error) {
      toast.error('Failed to delete fuel record');
    }
  };

  const handleExport = (exportType) => {
    if (exportType === 'month' && selectedMonth) {
      fuelAPI.exportExcel({ month: selectedMonth });
    } else {
      fuelAPI.exportExcel({ full_history: true });
    }
  };

  // Group records by vehicle for display
  const groupedRecords = records.reduce((acc, record) => {
    const reg = record.registration_number || 'Unknown';
    if (!acc[reg]) acc[reg] = [];
    acc[reg].push(record);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="fuel-schedule-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            FUEL SCHEDULE
          </h1>
          <p className="text-slate-500 mt-1">
            Track fuel consumption per vehicle. Consumption is calculated when the next fill is recorded.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={selectedMonth || 'all'} onValueChange={(v) => setSelectedMonth(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              {availableMonths.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline"
            onClick={() => handleExport(selectedMonth ? 'month' : 'full')}
            className="gap-2"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </Button>
          <Button 
            onClick={() => {
              setSelectedRecord(null);
              setFormDialogOpen(true);
            }}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            New Fill-up
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Records</p>
                  <p className="text-2xl font-bold">{statistics.total_records}</p>
                  {statistics.pending_calculation > 0 && (
                    <p className="text-xs text-amber-600">{statistics.pending_calculation} pending</p>
                  )}
                </div>
                <Fuel className="w-8 h-8 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Litres</p>
                  <p className="text-2xl font-bold">{statistics.total_litres?.toLocaleString()} L</p>
                </div>
                <Droplets className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total KM</p>
                  <p className="text-2xl font-bold">{statistics.total_km?.toLocaleString()}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Avg Consumption</p>
                  <p className={`text-2xl font-bold ${(statistics.avg_consumption || 0) >= STANDARD_CONSUMPTION ? 'text-emerald-600' : 'text-red-600'}`}>
                    {statistics.avg_consumption || 0} KM/L
                  </p>
                </div>
                <BarChart3 className="w-8 h-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-slate-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Vehicles</p>
                  <p className="text-2xl font-bold">{statistics.unique_vehicles}</p>
                </div>
                <Truck className="w-8 h-8 text-slate-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Standard</p>
                  <p className="text-2xl font-bold text-orange-600">{STANDARD_CONSUMPTION} KM/L</p>
                </div>
                <Gauge className="w-8 h-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Records ({records.length})</TabsTrigger>
          <TabsTrigger value="byVehicle">By Vehicle ({Object.keys(groupedRecords).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          {records.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Fuel className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No fuel records</h3>
                <p className="text-slate-500 mt-1 mb-4">Create a new fuel fill-up record to get started</p>
                <Button onClick={() => setFormDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Fill-up
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {records.map((record) => {
                const isPending = !record.consumption_calculated;
                return (
                  <Card 
                    key={record.id}
                    className={`hover:shadow-md transition-all cursor-pointer ${isPending ? 'border-l-4 border-l-amber-400' : ''}`}
                    data-testid={`fuel-record-${record.id}`}
                    onClick={() => {
                      setSelectedRecord(record);
                      setDetailDialogOpen(true);
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isPending ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                            <Fuel className={`w-5 h-5 ${isPending ? 'text-amber-600' : 'text-emerald-600'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold">{record.registration_number}</span>
                              <Badge variant="outline">{record.fill_date?.slice(0, 10)}</Badge>
                              {isPending && (
                                <Badge variant="outline" className="text-amber-600 border-amber-400">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-slate-500">
                              <span>
                                <User className="w-3 h-3 inline mr-1" />
                                {record.driver_name || 'N/A'}
                              </span>
                              <span>{record.litres_refuelled} L</span>
                              <span>@ {record.current_odometer?.toLocaleString()} km</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            {record.actual_consumption ? (
                              <>
                                <p className={`text-lg font-bold ${record.actual_consumption >= STANDARD_CONSUMPTION ? 'text-emerald-600' : 'text-red-600'}`}>
                                  {record.actual_consumption} KM/L
                                </p>
                                <p className="text-xs text-slate-500">{record.travelled_km?.toLocaleString()} km travelled</p>
                              </>
                            ) : (
                              <>
                                <p className="text-lg font-bold text-amber-600">Pending</p>
                                <p className="text-xs text-slate-500">Awaiting next fill</p>
                              </>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRecord(record);
                                setFormDialogOpen(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('Delete this record?')) {
                                  handleDelete(record);
                                }
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="byVehicle" className="mt-4">
          {Object.keys(groupedRecords).length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Truck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No records by vehicle</h3>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedRecords).map(([regNumber, vehicleRecords]) => {
                // Calculate averages for this vehicle (only from calculated records)
                const calculatedRecords = vehicleRecords.filter(r => r.consumption_calculated);
                const totalTravelled = calculatedRecords.reduce((sum, r) => sum + (r.travelled_km || 0), 0);
                const totalLitres = calculatedRecords.reduce((sum, r) => sum + (r.litres_refuelled || 0), 0);
                const avgConsumption = calculatedRecords.length > 0 
                  ? calculatedRecords.reduce((sum, r) => sum + (r.actual_consumption || 0), 0) / calculatedRecords.length 
                  : 0;
                const pendingCount = vehicleRecords.length - calculatedRecords.length;

                return (
                  <Card key={regNumber}>
                    <CardHeader className="bg-slate-50 border-b">
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Truck className="w-5 h-5 text-slate-600" />
                          <span className="font-mono">{regNumber}</span>
                          <Badge>{vehicleRecords.length} records</Badge>
                          {pendingCount > 0 && (
                            <Badge variant="outline" className="text-amber-600 border-amber-400">
                              {pendingCount} pending
                            </Badge>
                          )}
                        </div>
                        <div className="text-right text-sm font-normal">
                          <p>Avg: <span className={`font-bold ${avgConsumption >= STANDARD_CONSUMPTION ? 'text-emerald-600' : 'text-red-600'}`}>{avgConsumption.toFixed(2)} KM/L</span></p>
                          <p className="text-slate-500">Total: {totalTravelled.toLocaleString()} km | {totalLitres.toLocaleString()} L</p>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="text-left p-3">Date</th>
                            <th className="text-left p-3">Driver</th>
                            <th className="text-right p-3">Current Odo</th>
                            <th className="text-right p-3">Closing Odo</th>
                            <th className="text-right p-3">Travelled</th>
                            <th className="text-right p-3">Litres</th>
                            <th className="text-right p-3">Consumption</th>
                            <th className="text-center p-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vehicleRecords.map((record) => {
                            const isPending = !record.consumption_calculated;
                            return (
                              <tr key={record.id} className={`border-b hover:bg-slate-50 ${isPending ? 'bg-amber-50' : ''}`}>
                                <td className="p-3">{record.fill_date?.slice(0, 10)}</td>
                                <td className="p-3">{record.driver_name || 'N/A'}</td>
                                <td className="p-3 text-right">{record.current_odometer?.toLocaleString()}</td>
                                <td className="p-3 text-right">{record.closing_odometer ? record.closing_odometer.toLocaleString() : <span className="text-amber-600">—</span>}</td>
                                <td className="p-3 text-right font-medium">{record.travelled_km ? record.travelled_km.toLocaleString() : <span className="text-amber-600">—</span>}</td>
                                <td className="p-3 text-right">{record.litres_refuelled}</td>
                                <td className={`p-3 text-right font-bold ${isPending ? 'text-amber-600' : record.actual_consumption >= STANDARD_CONSUMPTION ? 'text-emerald-600' : 'text-red-600'}`}>
                                  {record.actual_consumption || '—'}
                                </td>
                                <td className="p-3 text-center">
                                  {isPending ? (
                                    <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs">Pending</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-emerald-600 border-emerald-400 text-xs">Calculated</Badge>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Form Dialog */}
      <FuelFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
        onSave={handleCreateRecord}
        vehicles={vehicles}
        drivers={drivers}
      />

      {/* Detail Dialog */}
      <FuelDetailDialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
        onEdit={(recordToEdit) => {
          setDetailDialogOpen(false);
          setTimeout(() => {
            setSelectedRecord(recordToEdit);
            setFormDialogOpen(true);
          }, 150);
        }}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default FuelSchedulePage;
